import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, Share2, Twitter, Linkedin, Facebook, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useBlogPost, useBlogPosts } from '../hooks/useContent';
import { RichTextRenderer } from '../components/RichTextRenderer';

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Fetch the specific blog post from Contentful (with static fallback)
  const { post, isLoading, error } = useBlogPost(slug);
  // Fetch all posts for related articles
  const { posts: allPosts } = useBlogPosts();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
        <span className="ml-2 text-slate-600">Loading article...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Error Loading Article
          </h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Article Not Found
          </h1>
          <p className="text-slate-600 mb-8">
            The article you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  const postTags = Array.isArray(post.tags) ? post.tags : [];
  const relatedPosts = allPosts
    .filter((p) => p.id !== post.id && (Array.isArray(p.tags) ? p.tags : []).some((tag) => postTags.includes(tag)))
    .slice(0, 3);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div data-testid="blog-detail-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-slate-600 hover:text-brand-primary mb-8 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>

            <div className="flex flex-wrap gap-2 mb-4">
              {postTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag}`}
                  className="text-xs font-medium text-brand-secondary bg-brand-secondary/10 px-3 py-1 rounded-full hover:bg-brand-secondary hover:text-white transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-primary leading-tight mb-6">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-white -mt-8 relative z-10">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl overflow-hidden shadow-xl"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[300px] md:h-[500px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg max-w-none"
          >
            {/* Check if content is Rich Text (from Contentful) or string (from static) */}
            {post.contentRaw && typeof post.contentRaw === 'object' ? (
              <RichTextRenderer content={post.contentRaw} />
            ) : typeof post.content === 'string' ? (
              post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2
                    key={index}
                    className="font-heading text-2xl md:text-3xl font-bold text-brand-primary mt-12 mb-4"
                  >
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3
                    key={index}
                    className="font-heading text-xl md:text-2xl font-bold text-brand-primary mt-8 mb-4"
                  >
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('- ')) {
                const items = paragraph.split('\n').filter((item) => item.startsWith('- '));
                return (
                  <ul key={index} className="list-disc list-inside space-y-2 my-6">
                    {items.map((item, i) => (
                      <li key={i} className="text-slate-600">
                        {item.replace('- ', '')}
                      </li>
                    ))}
                  </ul>
                );
              }
              if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return (
                  <p key={index} className="font-bold text-brand-primary my-4">
                    {paragraph.replace(/\*\*/g, '')}
                  </p>
                );
              }
              if (paragraph === '---') {
                return <hr key={index} className="my-12 border-slate-200" />;
              }
              return (
                <p key={index} className="text-slate-600 leading-relaxed my-4">
                  {paragraph}
                </p>
              );
            })
            ) : (
              <p className="text-slate-600">No content available.</p>
            )}
          </motion.div>

          {/* Share Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 pt-8 border-t border-slate-200"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Share2 className="w-5 h-5" />
                <span className="font-medium">Share this article</span>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors"
                  aria-label="Share on Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors"
                  aria-label="Share on Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 md:py-24 bg-brand-accent">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary mb-12">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <motion.article
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/blog/${relatedPost.slug}`} className="block">
                    <div className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="font-heading text-lg font-bold text-brand-primary group-hover:text-brand-secondary transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <div className="mt-4 text-xs text-slate-500">
                          {relatedPost.date} • {relatedPost.readTime}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
